import { useCallback, useContext, useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Ticket } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DataTable } from '../../components/DataTable';
import AppContext from '../../contexts/AppContext';
import { useAdminIssues } from '../../hooks/useAdminIssues';
import { useServiceDesks } from '../../hooks/useServiceDesks';
import type { AdminIssue, AdminIssuesFilters } from '../../api/adminIssuesApi';
import { IssuesServiceDeskSelect } from './components/IssuesServiceDeskSelect';
import { IssuesFiltersBar } from './components/IssuesFiltersBar';
import { getIssuesColumns } from './components/columns';
import { ValidateTransitionDialog } from './components/ValidateTransitionDialog';
import { IssuesTableSkeleton } from './components/IssuesTableSkeleton';
import { parseDeskAccess } from '../../utils/deskAccess';
import { apiErrorMessage } from '../../utils/errorMessage';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';
import UpcomingMissionsTab from './components/UpcomingMissionsTab';
import MissionAssignmentPanel from './components/MissionAssignmentPanel';
import { useMissions } from './useMissions';
import { useAdminIssuesFilterOptions } from '../../hooks/useAdminIssuesFilterOptions';
import { useAdminIssueStoreLabels } from '../../hooks/useAdminIssueStoreLabels';
import { qualificationFor, resolveRequestTypeNames } from '../../constants/qualificationDesks';

// Must match JIRA_PAGE_SIZE in backend/services/adminIssues.service.ts:
// only used to turn the total count into a page count.
const PAGE_SIZE = 50;

const DEFAULT_FILTER_VALUES: Omit<AdminIssuesFilters, 'serviceDeskId'> = {
  orderBy: 'updated',
  order: 'desc',
};

type CurrentUser = { role?: string; serviceDeskId?: number | string | (number | string)[] };

/**
 * The two views of the same listing. « À venir » adds the assignment columns, the summary line and
 * the drawer — it does not add a screen, a route or a second query for the missions themselves.
 */
type IssuesTab = 'all' | 'upcoming';

/** Literal translation calls only — the i18n static guard rejects a key built from a variable. */
const TABS: { id: IssuesTab; label: (t: TFunction) => string }[] = [
  { id: 'all', label: (t) => t('All tickets') },
  { id: 'upcoming', label: (t) => t('To qualify') },
];

/** The two filters the tab imposes. Cleared together when leaving it. */
const IMPOSED: Partial<AdminIssuesFilters> = { status: undefined, requestType: undefined };

/** Containment, not equality: the tab's values must all be there, extras are the user's. */
const containsAll = (current: string[] | undefined, imposed: string[] | undefined): boolean =>
  (imposed ?? []).every((value) => !!current?.includes(value));

type IssuesAppContext = { currentUser: CurrentUser | null; jiraBrowseBase: string | null };

// Mirrors the backend rule: superadmins (or a '*' wildcard) see every desk,
// everyone else only the numeric ids they are bound to.
const getDeskAccess = (currentUser: CurrentUser | null): { all: boolean; ids: Set<number> } => {
  const { all, ids } = parseDeskAccess(currentUser?.serviceDeskId);
  return { all: currentUser?.role === 'superadmin' || all, ids };
};

const pickInitialServiceDeskId = (currentUser: CurrentUser | null): number => {
  const [first] = Array.from(getDeskAccess(currentUser).ids);
  return first ?? 0;
};

// Seeds the filters from the URL (?sd=<deskId>&search=<key>) so the validation emails can
// deep-link a ticket; an sd the user cannot access is snapped back by the desk-options effect.
const buildInitialFilters = (
  currentUser: CurrentUser | null,
  params: URLSearchParams
): AdminIssuesFilters => {
  const sd = Number(params.get('sd'));
  const search = params.get('search')?.trim();
  return {
    serviceDeskId: Number.isInteger(sd) && sd > 0 ? sd : pickInitialServiceDeskId(currentUser),
    ...DEFAULT_FILTER_VALUES,
    ...(search ? { search } : {}),
  };
};

const AdminIssues = () => {
  const { t } = useTranslation();
  const { currentUser, jiraBrowseBase } = useContext(AppContext) as IssuesAppContext;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<AdminIssuesFilters>(() =>
    buildInitialFilters(currentUser, searchParams)
  );

  const { serviceDesks, isLoading: isLoadingServiceDesks } = useServiceDesks();
  const deskAccess = useMemo(() => getDeskAccess(currentUser), [currentUser]);
  const serviceDeskOptions = useMemo(
    () =>
      serviceDesks
        .filter((sd) => sd.enabled !== false)
        .filter((sd) => deskAccess.all || deskAccess.ids.has(sd.id))
        .map((sd) => ({ id: sd.id, label: sd.name })),
    [serviceDesks, deskAccess]
  );

  // Cursor pagination: pageTokens[i] is the Jira token that loads page i (page 0 = null).
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState<number | null>(null);

  // Any filter change restarts pagination. Done in the same update as the filter change:
  // an effect would run a render too late, firing one request that pairs the new filters
  // with the previous JQL's cursor.
  const resetPagination = useCallback(() => {
    setPageTokens([null]);
    setPageIndex(0);
    setTotalPages(null);
  }, []);

  // Once the SD list arrives, snap the current selection to a valid entry
  // (the user's primary SD if available, otherwise the first option).
  useEffect(() => {
    if (!serviceDeskOptions.length) return;
    if (serviceDeskOptions.some((opt) => opt.id === filters.serviceDeskId)) return;
    const primary = pickInitialServiceDeskId(currentUser);
    const next = serviceDeskOptions.some((opt) => opt.id === primary)
      ? primary
      : serviceDeskOptions[0].id;
    setFilters((prev) => ({ ...prev, serviceDeskId: next }));
    resetPagination();
  }, [serviceDeskOptions, filters.serviceDeskId, currentUser, resetPagination]);

  // Stable so the search debounce in IssuesFiltersBar isn't reset on every render.
  const updateFilters = useCallback((partial: Partial<AdminIssuesFilters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    resetPagination();
  }, [resetPagination]);

  const resetFilters = () => {
    setFilters((prev) => ({
      serviceDeskId: prev.serviceDeskId,
      ...DEFAULT_FILTER_VALUES,
    }));
    resetPagination();
  };

  const { data, isLoading, error } = useAdminIssues({
    ...filters,
    pageToken: pageTokens[pageIndex] ?? undefined,
  });
  const [issueToValidate, setIssueToValidate] = useState<AdminIssue | null>(null);
  /**
   * `?tab=upcoming` seeds it, so the follow-up screen can land on the qualification tab — and
   * `?assign=<key>` then opens the drawer on one mission. Local state from there on: the tab is a
   * view, and only the arrival has to be addressable.
   */
  const [tab, setTab] = useState<IssuesTab>(() =>
    searchParams.get('tab') === 'upcoming' ? 'upcoming' : 'all'
  );

  const canManageTechnicians = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_MANAGE);
  const canViewTechnicians = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW);

  /*
    What this desk calls « À qualifier ». `null` for a desk with no qualification workflow
    identified — the tab is then hidden rather than shown empty (D13).
  */
  const qualification = qualificationFor(filters.serviceDeskId);
  /*
    Whether this desk writes the qualification workflow into Jira (D17). Read here, where the desk
    list already is, and passed down — the flag ships OFF, so a second consumer of the hook would
    be a second place for that default to be got wrong.
  */
  const deskQualifiesInJira = serviceDesks.some(
    (sd) => sd.id === filters.serviceDeskId && sd.hasInterventionQualification === true
  );
  // Already fetched by the filters bar for the same desk; react-query serves it from cache.
  const { data: filterOptions } = useAdminIssuesFilterOptions(filters.serviceDeskId);
  // A tab that answers 403, or one that can only ever be empty, is worse than no tab.
  const showQualifyTab = canViewTechnicians && qualification !== null;
  const imposedFilters = useMemo((): Partial<AdminIssuesFilters> | null => {
    if (!qualification) return null;
    const requestType = resolveRequestTypeNames(qualification, filterOptions?.requestTypes);
    return requestType ? { status: [qualification.statusId], requestType } : null;
  }, [qualification, filterOptions?.requestTypes]);
  /*
    « Tous les tickets » is the only door left to a mission a send has scheduled: it leaves
    « À qualifier » at once (D17) and the follow-up is strict consultation (D36). Same join as the
    other tab — react-query hashes keys structurally, so the two calls are one request (C5).
  */
  const listed = useMemo(() => data?.issues ?? [], [data?.issues]);

  // Sorted and deduped, so the same page hits the same cache entry on every visit.
  const storeIds = useMemo(
    () =>
      Array.from(
        new Set(listed.map((issue) => issue.storeObjectId).filter((id): id is string => !!id))
      ).sort(),
    [listed]
  );
  const { labels: storeLabels, isLoading: storeLabelsLoading } = useAdminIssueStoreLabels(
    filters.serviceDeskId,
    storeIds
  );

  // Reconciled here rather than in the columns: everything downstream — the two tabs, the
  // missions, the CSV export — keeps reading `issue.store` and knows nothing of the split.
  const issues = useMemo(
    () =>
      listed.map((issue) =>
        issue.store || !issue.storeObjectId
          ? issue
          : { ...issue, store: storeLabels[issue.storeObjectId] ?? null }
      ),
    [listed, storeLabels]
  );
  const reassignAllowed = canManageTechnicians && deskQualifiesInJira && qualification !== null;
  const { missions, assignmentsKnown, error: assignmentsError } = useMissions(
    filters.serviceDeskId,
    issues,
    { enabled: reassignAllowed }
  );
  const [reassigningKey, setReassigningKey] = useState<string | null>(null);
  // ⚠️ Never an `AdminIssue`: the panel dereferences `mission.specialties`.
  const reassigning = missions.find((mission) => mission.issueKey === reassigningKey) ?? null;

  const reassignAction = useMemo(() => {
    // `assignmentsKnown` is not decoration: during a `keepPreviousData` render every row reads
    // `liveRequestCount: 0`, and the action would be offered on missions that have no live request.
    if (!reassignAllowed || !qualification || !assignmentsKnown) return undefined;
    const liveByKey = new Map(missions.map((mission) => [mission.issueKey, mission.liveRequestCount]));
    return {
      // The back's own condition, not a cosmetic one: on a ticket scheduled with no live request
      // `ourOwn` is false and the send answers 409. Such a ticket was programmed by hand in Jira,
      // and reopening it is not ours to offer (C2).
      canReassign: (issue: AdminIssue) =>
        issue.statusId === qualification.scheduledStatusId && (liveByKey.get(issue.issueKey) ?? 0) > 0,
      onReassign: (issue: AdminIssue) => setReassigningKey(issue.issueKey),
    };
  }, [reassignAllowed, qualification, missions, assignmentsKnown]);

  const columns = useMemo(
    () =>
      getIssuesColumns(
        t,
        setIssueToValidate,
        (issue) => navigate(`/admin/issues/${encodeURIComponent(issue.issueKey)}`),
        jiraBrowseBase,
        reassignAction,
        storeLabelsLoading
      ),
    [t, jiraBrowseBase, navigate, reassignAction, storeLabelsLoading]
  );

  // The desk selector only makes sense when the user actually has a choice: superadmins (all desks)
  // or users bound to several. A single-desk user sees only their own desk's issues, no picker.
  const showDeskSelector = deskAccess.all || deskAccess.ids.size > 1;

  // The total count comes back only on the first page; keep it for the whole run.
  useEffect(() => {
    if (data?.total != null) setTotalPages(Math.max(1, Math.ceil(data.total / PAGE_SIZE)));
  }, [data?.total]);

  // One pagination object for both tabs: they are the same cursor over the same listing, and two
  // copies would be two places to forget `totalCount` — without which the footer counts the page
  // and announces "100 of 100".
  const pagination = {
    pageIndex,
    pageCount: totalPages,
    totalCount: data?.total,
    hasNextPage: !!data?.nextPageToken,
    onPreviousPage: () => goToPrevPage(),
    onNextPage: () => goToNextPage(),
  };

  /**
   * The tab imposes its own definition, and takes it back on the way out.
   *
   * These two filters are not the user's: they are what « À qualifier » *means* (D13). They are
   * posted as real filters so the listing, the count and the CSV all agree, and they are cleared
   * when leaving — L4b left `statusCategory` stuck on « Tous les tickets », which quietly changed
   * what the other tab showed.
   *
   * Nothing is imposed until `filter-options` has resolved the ids to this desk's names: JQL
   * matches request types by name, and a half-resolved list would widen the tab to everything.
   */
  const selectTab = (next: IssuesTab) => {
    setTab(next);
    // The panel belongs to the listing it was opened from; leaving that listing closes it.
    setReassigningKey(null);
    if (next === 'all') {
      updateFilters(IMPOSED);
      return;
    }
    if (imposedFilters) updateFilters(imposedFilters);
  };

  /*
    Applied from an effect as well as from the click: on a first load the options are still in
    flight when the tab is picked, and on a desk change the names are the other desk's. Both cases
    have to re-impose, and neither is a click.
  */
  useEffect(() => {
    if (tab !== 'upcoming' || !imposedFilters) return;
    // Comparing lists for equality re-imposed — and so wiped — every value the user added on
    // top of the tab's own (D18 amended): only a *missing* imposed value is drift.
    if (
      containsAll(filters.status, imposedFilters.status) &&
      containsAll(filters.requestType, imposedFilters.requestType)
    ) {
      return;
    }
    updateFilters(imposedFilters);
  }, [tab, imposedFilters, filters.status, filters.requestType, updateFilters]);

  // Switching to a desk with no qualification workflow removes the tab; staying on it would show
  // a panel whose own tab is gone, and the imposed filters with it.
  useEffect(() => {
    if (tab === 'upcoming' && !showQualifyTab) {
      setTab('all');
      updateFilters(IMPOSED);
    }
  }, [tab, showQualifyTab, updateFilters]);

  /** Left/right (and Home/End) move between tabs — the other half of the roving tabindex. */
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = TABS.findIndex((entry) => entry.id === tab);
    const next =
      event.key === 'ArrowRight' ? (index + 1) % TABS.length
      : event.key === 'ArrowLeft' ? (index - 1 + TABS.length) % TABS.length
      : event.key === 'Home' ? 0
      : event.key === 'End' ? TABS.length - 1
      : -1;
    if (next < 0) return;
    event.preventDefault();
    selectTab(TABS[next].id);
    document.getElementById(`issues-tab-${TABS[next].id}`)?.focus();
  };

  const goToNextPage = () => {
    const token = data?.nextPageToken;
    if (!token) return;
    setPageTokens((prev) => [...prev.slice(0, pageIndex + 1), token]);
    setPageIndex((i) => i + 1);
  };
  const goToPrevPage = () => setPageIndex((i) => Math.max(0, i - 1));

  return (
    <div className="container mx-auto p-6 space-y-4">
      {/* The same spelling the other admin screens give their main zone — see
          `AdminInterventionRequests` and `AdminTechnicians`: the shared `Card` for the border and
          the shadow, `rounded-[14px]` for the corner they all draw. */}
      <Card className="rounded-[14px]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Ticket className="w-6 h-6 text-primary" />
            <div>
              <CardTitle>{t('Issues')}</CardTitle>
              <p className="text-sm text-gray-500">
                {t('Overview of Jira issues by service desk')}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/*
            Two views of one listing, and a plain tablist rather than a Radix Tabs: two buttons and
            an `aria-selected` are the whole widget, and there is no Tabs primitive in the kit to
            reuse. Hidden entirely without `technicians:view` — a tab that answers 403 is worse
            than no tab.
          */}
          {showQualifyTab && (
            /* The mock-up's segmented control, not an underline. Every ARIA behaviour below is
               kept as it is — roving tabindex, arrow keys, `aria-selected` — and is better than
               the mock-up, which carries no roles at all. Only the skin changes. */
            <div
              role="tablist"
              aria-label={t('Views')}
              className="inline-flex items-center gap-0.5 rounded-[10px] border p-[3px]"
            >
              {TABS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`issues-tab-${id}`}
                  aria-selected={tab === id}
                  aria-controls={`issues-panel-${id}`}
                  // Roving tabindex plus arrow keys: the ARIA Tabs pattern puts one stop in the
                  // tab order and moves between tabs with the arrows. Without it the tablist
                  // announces itself as tabs and behaves as two ordinary buttons.
                  tabIndex={tab === id ? 0 : -1}
                  onKeyDown={onTabKeyDown}
                  onClick={() => selectTab(id)}
                  className={`h-8 rounded-lg px-[13px] text-[13px] transition-colors ${
                    tab === id
                      ? 'bg-muted font-semibold text-foreground'
                      : 'font-medium text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label(t)}
                </button>
              ))}
            </div>
          )}

          <IssuesFiltersBar
            filters={filters}
            onChange={updateFilters}
            onReset={resetFilters}
            // The values, so the bar can refuse to remove them. `{}` while `filter-options` has
            // not resolved the names yet: locked, with nothing locked in it.
            lockedByTab={tab === 'upcoming' ? imposedFilters ?? {} : null}
            // The page still owns the desks; the bar only places the control in its compact row.
            deskSelect={
              showDeskSelector ? (
                <IssuesServiceDeskSelect
                  value={filters.serviceDeskId}
                  // Sector/store options are per-desk assets: a stale selection from the
                  // previous desk would silently filter to zero rows while its field hides.
                  onChange={(id) => updateFilters({ serviceDeskId: id, sector: undefined, store: undefined })}
                  options={serviceDeskOptions}
                  isLoading={isLoadingServiceDesks}
                />
              ) : undefined
            }
          />

          {/*
            `aria-labelledby` only when the tab it names is actually rendered: without
            `technicians:view` the tablist is absent, and pointing at an id no longer in the DOM
            leaves a screen reader announcing a panel labelled by nothing.
          */}
          <div
            role="tabpanel"
            id={`issues-panel-${tab}`}
            aria-labelledby={showQualifyTab ? `issues-tab-${tab}` : undefined}
            aria-label={showQualifyTab ? undefined : t('Issues')}
          >
            {tab === 'upcoming' && showQualifyTab ? (
              <UpcomingMissionsTab
                serviceDeskId={filters.serviceDeskId}
                issues={issues}
                isLoading={isLoading}
                storeLabelsLoading={storeLabelsLoading}
                error={(error as Error) ?? null}
                canManage={canManageTechnicians}
                hasInterventionQualification={deskQualifiesInJira}
                jiraBrowseBase={jiraBrowseBase}
                serverPagination={pagination}
              />
            ) : isLoading ? (
              <IssuesTableSkeleton />
            ) : error ? (
              <p className="text-sm text-red-600">{(error as Error).message}</p>
            ) : (
              <>
                {/* Said, never swallowed: without it a failed assignment read takes every
                    re-solicitation action off the table while the table itself looks healthy,
                    and nothing tells an ineligible mission from an outage. */}
                {assignmentsError && (
                  <p className="pb-3 text-[13px] text-rose-600 dark:text-rose-400" role="alert">
                    {apiErrorMessage(t, assignmentsError)}
                  </p>
                )}
                <DataTable
                  data={issues}
                  columns={columns}
                  emptyMessage={t('No issue')}
                  serverPagination={pagination}
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <ValidateTransitionDialog
        issue={issueToValidate}
        onClose={() => setIssueToValidate(null)}
      />

      {/* The same panel « À qualifier » opens, and unchanged: on a scheduled ticket the date is
          already read-only and the technicians already solicited are already greyed (C4). On a
          mission somebody accepted it offers « Libérer la mission », which is the only way back
          when the technician retained drops out (C3). */}
      <MissionAssignmentPanel
        mission={reassigning}
        serviceDeskId={filters.serviceDeskId}
        hasInterventionQualification={deskQualifiesInJira}
        onClose={() => setReassigningKey(null)}
      />
    </div>
  );
};

export default AdminIssues;
